import Phaser from 'phaser';
import type { AssetManifest } from '@repo/core-phaser-wrapper';

/**
 * PreloaderScene - Loads game assets with a progress bar
 *
 * Displays a loading bar and loads assets from the asset manifest
 * stored in the game registry. Transitions to the first game scene
 * when loading is complete.
 */
export class PreloaderScene extends Phaser.Scene {
  private loadingBar!: Phaser.GameObjects.Graphics;
  private progressBar!: Phaser.GameObjects.Graphics;
  private loadingText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'PreloaderScene' });
  }

  preload(): void {

    // Background
    this.cameras.main.setBackgroundColor('#000000');

    // Create loading bar graphics
    this.createLoadingBar();

    // Load assets from manifest (if provided)
    const assetManifest = this.registry.get('assetManifest');
    if (assetManifest) {
      this.loadAssets(assetManifest);
    }

    // Update loading bar as assets load
    this.load.on('progress', (value: number) => {
      this.updateLoadingBar(value);
    });

    this.load.on('complete', () => {
      this.loadingText.setText('Ready!');
    });
  }

  create(): void {
    // Short delay before transitioning
    this.time.delayedCall(500, () => {
      this.transitionToGame();
    });
  }

  /**
   * Create the loading bar UI
   */
  private createLoadingBar(): void {
    const { width, height } = this.cameras.main;

    // Loading text
    this.loadingText = this.add.text(width / 2, height / 2 - 50, 'Loading...', {
      fontSize: '20px',
      color: '#ffffff',
    });
    this.loadingText.setOrigin(0.5);

    // Loading bar background (gray box)
    this.loadingBar = this.add.graphics();
    this.loadingBar.fillStyle(0x222222, 0.8);
    this.loadingBar.fillRect(width / 4, height / 2, width / 2, 30);

    // Progress bar (green)
    this.progressBar = this.add.graphics();
  }

  /**
   * Update the loading bar progress
   */
  private updateLoadingBar(value: number): void {
    const { width, height } = this.cameras.main;

    this.progressBar.clear();
    this.progressBar.fillStyle(0x00ff00, 1);
    this.progressBar.fillRect(width / 4 + 5, height / 2 + 5, (width / 2 - 10) * value, 20);

    // Update percentage text
    this.loadingText.setText(`Loading... ${Math.floor(value * 100)}%`);
  }

  /**
   * Load assets from the manifest
   */
  private loadAssets(manifest: AssetManifest): void {
    // Load images
    if (manifest.images) {
      manifest.images.forEach((img) => {
        this.load.image(img.key, img.path);
      });
    }

    // Load spritesheets
    if (manifest.spritesheets) {
      manifest.spritesheets.forEach((sheet) => {
        this.load.spritesheet(sheet.key, sheet.path, sheet.frameConfig);
      });
    }

    // Load audio
    if (manifest.audio) {
      manifest.audio.forEach((audio) => {
        this.load.audio(audio.key, audio.path);
      });
    }

    // Load JSON
    if (manifest.json) {
      manifest.json.forEach((json) => {
        this.load.json(json.key, json.path);
      });
    }
  }

  /**
   * Transition to the first game scene
   */
  private transitionToGame(): void {
    const firstGameScene = this.registry.get('firstGameScene') || 'GameScene';
    this.scene.start(firstGameScene);
  }
}
